import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode, type CSSProperties } from "react";
import { requestTikzSplitOnInsert } from "@/lib/tikzFigure";
import { outlineOf, sectionAt, setFigureScope, TIKZ_DRAW_WITH_ASK_EVENT } from "@/lib/tikzAsk";
import { prefillAsk } from "@/lib/askPrefill";
import { usePhoneReadOnly } from "@/hooks/useMobileMode";
import { useCanEdit } from "@/lib/documentAccess";
import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import type { Block } from "@/lib/api";
import type { BlockType } from "./BlockTypeMenu";
// AI assistant fully deactivated for launch — surface and hooks left
// in the codebase for post-launch revival under the Pro SKU.
// import { AIAssistantPanel } from "./AIAssistantPanel";
import { GrammarPanel } from "./GrammarPanel";
import { SlashCommandMenu, type SlashCommand } from "./SlashCommandMenu";
import { IconLetterCase, IconTableImport } from "@tabler/icons-react";
import {
  effectiveDocSettings,
  generate,
  isLoremSlashPrefix,
  LOREM_FIRED_EVENT,
  type LoremFiredEventDetail,
  parseLoremSlash,
  resolveLoremSlash,
} from "@/lib/lorem";
import {
  TOOLBOX_INSERT_BATCH_EVENT,
  openTablePicker,
  type ToolboxInsertBatchEventDetail,
} from "@/components/toolbox/insertHandler";
import { useToast } from "@/components/shared/Toast";
import { MobileSelectionPopover } from "./MobileSelectionPopover";
// MobileMoreFormatSheet removed (PF.T02 — final-road v1).
import { StatusBar } from "./StatusBar";
import { RefPicker } from "./refPicker/RefPicker";
// import { useAIAssistant } from "@/hooks/useAIAssistant";
import { useGrammarCheck } from "@/hooks/useGrammarCheck";
import { useEditorStore } from "@/store/editorStore";
import { useApiClient } from "@/lib/api-client";
import { useEditorContext } from "@/contexts/EditorContext";
import { useStudioFormatting } from "@/contexts/StudioFormattingContext";
import { getDocumentEditorExtensions } from "@/lib/tiptap";
import {
  consumeSlashTrigger,
  dismissSlashTrigger,
  readSlashTrigger,
  takeSlashInsertRange,
  afterCurrentBlock,
} from "@/lib/tiptap/extensions/slashTrigger";
import { blocksToTiptapDoc, blockToTiptapNode, tiptapDocToBlocks } from "@/lib/tiptap/document-serializer";
import type { JSONContent } from "@tiptap/core";
import { getDefaultContent } from "./useBlockTypes";
import { generateUUID } from "@/utils/uuid";
import styles from "./BlockEditor.module.scss";
import { canvasPageAttrs, type CanvasLook } from "@/lib/themes/look";
import { testIds } from "@/testIds";
import { useThemeStore } from "@/store/themeStore";
import { MODE_DARK } from "@/constants/theme.constant";
import { CanvasKicker } from "./CanvasKicker";
import { PasteFixChip } from "./pasteFix/PasteFixChip";
import { usePasteFix } from "./pasteFix/usePasteFix";
import { convertPastedMath } from "@/lib/mathPaste";
import { applyRebase } from "@/lib/sync/applyRebase";
import { useReferenceIndex } from "./references/useReferenceIndex";

interface BlockEditorProps {
  showSourceTabs?: boolean;
  /** The document theme on the canvas: heading face and colour, paper and ink (null = Classic). */
  look?: CanvasLook | null;
}

/** True when the store holds real document content (not just a blank slate). */
function blocksHaveRealContent(blocks: Block[]): boolean {
  if (blocks.length === 0) return false;
  if (blocks.length === 1 && blocks[0].type === "paragraph") {
    const text = (blocks[0].content as { text?: string } | undefined)?.text;
    return !!text?.trim();
  }
  // Any non-paragraph block, or multiple blocks → document is not empty.
  return true;
}

/** Map block types to their TipTap node type names for insertion */
const BLOCK_TYPE_TO_NODE: Record<string, string> = {
  paragraph: "paragraph",
  heading: "heading",
  equation: "equationBlock",
  figure: "figureBlock",
  code: "codeBlockNode",
  theorem: "theoremBlock",
  table: "tableBlock",
  abstract: "abstractBlock",
  title: "titleBlock",
  bibliography: "bibliographyBlock",
  tableOfContents: "tocBlock",
  pageBreak: "pageBreakBlock",
  columnBreak: "columnBreakBlock",
  list: "listBlock",
  blockquote: "blockquoteBlock",
  footnote: "footnoteBlockNode",
  embed: "embedBlock",
  algorithm: "algorithmBlock",
  callout: "calloutBlock",
  verse: "verseBlock",
  aside: "asideBlock",
  frontMatter: "frontMatterBlock",
  backMatter: "backMatterBlock",
  annotation: "annotationBlock",
  cover: "coverBlock",
  chapterBreak: "chapterBreakBlock",
};

/** Build default attrs for inserting a new block node */
function getInsertAttrs(blockType: string): Record<string, unknown> {
  const defaults = getDefaultContent(blockType as BlockType);
  return { blockId: generateUUID(), ...defaults };
}

/**
 * Grammar check's Apply: the first place the original wording appears in the document's text
 * becomes the suggestion, keeping the marks where it starts. Wording split across marks is left
 * as it is.
 */
function replaceFirst(editor: NonNullable<ReturnType<typeof useEditor>>, original: string, suggestion: string) {
  if (!original) return;
  let at: { from: number; to: number } | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (at || !node.isText || !node.text) return !at;
    const i = node.text.indexOf(original);
    if (i >= 0) at = { from: pos + i, to: pos + i + original.length };
    return false;
  });
  if (!at) return;
  const { from, to } = at;
  // insertText keeps the marks where the wording starts, and treats the suggestion as text.
  editor.view.dispatch(editor.state.tr.insertText(suggestion, from, to));
}

export function BlockEditor({ showSourceTabs = false, look = null }: BlockEditorProps) {
  // Dark editor: the page is a dark stand-in for the paper (data-paper-mode).
  const darkEditor = useThemeStore((s) => s.mode) === MODE_DARK;
  const readOnly = !useCanEdit();
  const phoneReadOnly = usePhoneReadOnly();
  const blocks = useEditorStore((s) => s.blocks);
  const documentId = useEditorStore((s) => s.documentId);
  const document = useEditorStore((s) => s.document);
  const title = useEditorStore((s) => s.title);
  const setEditorInstance = useEditorStore((s) => s.setEditorInstance);

  const editorRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { fetchWithAuth } = useApiClient();
  // Set once usePasteFix has the editor; handlePaste is configured before it.
  const onPastedTextRef = useRef<((text: string) => void) | null>(null);
  const libraryForPicker = useMemo(
    () =>
      (document?.bibliography || []).map((b) => {
        const f = (b.data || b.fields || {}) as Record<string, string>;
        return { citeKey: b.citeKey, author: f.author, year: f.year, title: f.title };
      }),
    [document?.bibliography],
  );
  // AI deactivated for launch — see top-of-file comment.
  const grammar = useGrammarCheck();
  const { setActiveEditor } = useEditorContext();
  const { showToast } = useToast();

  // Slash-menu lorem command. Filtered into the menu when the user
  // types /lorem / /lorem-tech / /lorem-math (matched via keywords).
  // On Enter, parses the live query for an optional count override and
  // dispatches the same batch event the chip/seed/header use.
  const loremSlashCommand = useMemo<SlashCommand>(() => ({
    id: "lorem",
    label: "Lorem ipsum",
    description: "Insert N placeholder paragraphs at cursor",
    keywords: ["lorem", "lorem ipsum", "lorem-tech", "lorem-math", "placeholder"],
    icon: IconLetterCase,
    // `/lorem 3` is the command and its count; "lorem 3" is in no keyword.
    matches: (query: string) => parseLoremSlash(`/${query}`) !== null,
    run: async (query: string) => {
      if (!documentId) return;
      const parsed = parseLoremSlash(query) ?? parseLoremSlash(`/${query}`);
      // Fallback to defaults if the user picked the entry without
      // typing a full /lorem command (e.g. selected from the filtered menu).
      const docSettings = effectiveDocSettings(documentId);
      const resolved = parsed
        ? resolveLoremSlash(parsed, docSettings)
        : { source: docSettings.source, count: docSettings.count, clamped: false };
      const paragraphs = await generate(resolved.source, resolved.count);
      const detail: ToolboxInsertBatchEventDetail = {
        calls: paragraphs.map((text) => ({
          type: "paragraph",
          content: { text },
        })),
        source: "lorem",
        atSlash: true,
      };
      window.dispatchEvent(
        new CustomEvent(TOOLBOX_INSERT_BATCH_EVENT, { detail }),
      );
      const fireDetail: LoremFiredEventDetail = {
        surface: "slash",
        source: resolved.source,
        count: resolved.count,
        clamped: resolved.clamped,
      };
      window.dispatchEvent(
        new CustomEvent(LOREM_FIRED_EVENT, { detail: fireDetail }),
      );
      if (resolved.clamped) {
        showToast("Capped at 25 paragraphs", "info");
      }
      // Silence the unused-prefix-checker import — kept exported so
      // external callers can re-use the same matcher the menu uses.
      void isLoremSlashPrefix;
    },
  }), [documentId, showToast]);

  /** One of the author's Tables, into this paper where the `/` was. The page
   *  hosts the picker (EditorTablePicker); the menu has already consumed the
   *  typed text, and remembered where the table goes. */
  const existingTableSlashCommand = useMemo<SlashCommand>(() => ({
    id: "existing-table",
    label: "Existing table…",
    description: "One of your tables — link it or take a copy",
    keywords: ["existing table", "table", "tables", "reuse", "link table"],
    icon: IconTableImport,
    run: () => openTablePicker({ atSlash: true }),
  }), []);
  // Register with the StudioFormattingContext so the desktop Format
  // rail panel (Mod+4) can find the active editor. Without this,
  // FormatRailPanel's tile clicks no-op silently because activeTiptap
  // stays null. EditorContext was the original wiring; this second
  // registration coexists.
  const { registerTiptap, unregisterTiptap } = useStudioFormatting();

  // Slash command state
  const [slashState, setSlashState] = useState<{
    show: boolean;
    query: string;
    anchor: HTMLElement | null;
    caretCoords: { top: number; left: number; bottom: number } | null;
  }>({ show: false, query: "", anchor: null, caretCoords: null });

  // Initialize editor content from blocks (only on first mount)
  const initialContent = useMemo(() => blocksToTiptapDoc(blocks), []);

  // Empty-document flag — drives the flow empty-state ghost hint (Claude Design
  // handoff 2026-06-17). TipTap's `editor.isEmpty` alone is not enough: after
  // external setContent (doc load) it can lag, and atom-only docs can look
  // "empty" while the canvas clearly has blocks. Combine editor + block model.
  const [editorEmpty, setEditorEmpty] = useState(true);
  const blocksHaveContent = useMemo(() => blocksHaveRealContent(blocks), [blocks]);
  const isEmpty = !blocksHaveContent && editorEmpty;

  // Marks canvas-local focus changes so the focus effect doesn't re-scroll
  // when the user already clicked/typed in the editor (source → canvas still scrolls).
  const lastLocalFocusClickRef = useRef<number>(0);

  // Debounced sync of blocks back to store for selectors (headings, theorems, etc.)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncBlocksToStore = useCallback((ed: Editor) => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const serialized = tiptapDocToBlocks(ed.getJSON());
      // A lone empty paragraph = an empty document — don't persist it as a block,
      // so block mode shows the empty-state hero rather than a stray paragraph.
      const effective =
        serialized.length === 1 &&
        serialized[0].type === "paragraph" &&
        !((serialized[0].content as { text?: string })?.text?.trim())
          ? []
          : serialized;
      // Direct set without pushing undo — this is a sync, not a user action
      useEditorStore.setState({ blocks: effective });
    }, 300);
  }, []);

  const editor = useEditor({
    extensions: getDocumentEditorExtensions(),
    content: initialContent,
    // A viewer reads; the API would refuse their saves anyway. On a phone everyone
    // reads: no caret, no keyboard from a tap; changes come through Ask Lilia.
    editable: !readOnly && !phoneReadOnly,
    // Flow mode already renders a transient empty paragraph for an empty doc
    // (blocksToTiptapDoc ensures one) — typeable immediately. We deliberately do
    // NOT persist it as a block: that would leave a stray empty paragraph in
    // block mode instead of the empty-state hero. It becomes a real block only
    // once the user types (onUpdate → syncBlocksToStore).
    onCreate: ({ editor }) => setEditorEmpty(editor.isEmpty),
    onUpdate: ({ editor }) => {
      // Empty-doc ghost hint: hide as soon as the user types.
      setEditorEmpty(editor.isEmpty);

      // Mark document as dirty and bump edit version for auto-save subscription
      useEditorStore.setState((s) => ({ isDirty: true, editVersion: s.editVersion + 1 }));

      // Sync blocks to store (debounced) for selectors
      syncBlocksToStore(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      const selection = editor.state.selection;
      // NodeSelection: clicking on an atom/node-view block
      if ("node" in selection && (selection as { node: { attrs?: { blockId?: string } } }).node?.attrs?.blockId) {
        // Mark as local so the focus effect doesn't re-scroll the canvas.
        lastLocalFocusClickRef.current = Date.now();
        useEditorStore.getState().setFocusedBlockId(
          (selection as { node: { attrs: { blockId: string } } }).node.attrs.blockId,
        );
        return;
      }
      // TextSelection: cursor inside a paragraph/heading
      const { $from } = selection;
      for (let d = $from.depth; d >= 0; d--) {
        const node = $from.node(d);
        if (node.attrs?.blockId) {
          lastLocalFocusClickRef.current = Date.now();
          useEditorStore.getState().setFocusedBlockId(node.attrs.blockId);
          return;
        }
      }
    },
    editorProps: {
      attributes: {
        class: "lilia-document-editor",
        spellcheck: "true",
        style: "outline: none;",
      },
      // Maths copied from KaTeX, MathJax, Wikipedia, MathML or Word becomes Lilia maths (18 Oct).
      transformPastedHTML: (html) => convertPastedMath(html),
      handlePaste: (_view, event) => {
        // Paste-to-fix only listens: the paste itself proceeds untouched.
        const pastedText = event.clipboardData?.getData("text/plain");
        if (pastedText) onPastedTextRef.current?.(pastedText);
        // Ctrl+Shift+V / Cmd+Shift+V pastes as plain text: ProseMirror does it when Shift is held
        // (a paste event carries no shiftKey of its own, so it can't be checked here).
        return false;
      },
    },
  });

  // The role can arrive after the editor mounts (the document loads later).
  // emitUpdate=false: setEditable emits "update" by default, which onUpdate
  // takes for an edit — marking the store dirty (so the loaded content is
  // never applied) and syncing the still-empty canvas over the blocks.
  useEffect(() => {
    const editable = !readOnly && !phoneReadOnly;
    if (editor && !editor.isDestroyed && editor.isEditable !== editable) editor.setEditable(editable, false);
  }, [editor, readOnly, phoneReadOnly]);

  const pasteFix = usePasteFix(editor, fetchWithAuth);
  // EditorPage publishes save times through statusMeta; a save is what makes a
  // new label visible to the index.
  const lastSavedAt = useEditorStore((s) => s.statusMeta?.lastSavedAt?.getTime() ?? null);
  useReferenceIndex(editor, documentId ?? undefined, fetchWithAuth, lastSavedAt);
  onPastedTextRef.current = (text) => void pasteFix.onPaste(text);
  const lookUpSymbol = useCallback((char: string) => {
    // The host owns the rail; it opens Symbols with the character searched.
    window.dispatchEvent(new CustomEvent("lilia:symbols-lookup", { detail: { query: char } }));
  }, []);

  // Register editor with store and EditorContext
  useEffect(() => {
    if (editor) {
      setActiveEditor(editor);
      setEditorInstance(editor);
      // Also register with StudioFormattingContext so the desktop
      // Format rail panel (Mod+4) sees this as the active editor and
      // its tile clicks fire toggleMark / setMark on the right
      // ProseMirror instance. Without this, the panel commands
      // no-op silently because activeTiptap stays null in Flow mode.
      registerTiptap(editor);
      // Auto-focus on desktop so the cursor is visible immediately, but
      // NOT on touch devices where it pops the virtual keyboard and
      // covers half the viewport — overwhelming on first open.
      // Detect via pointer/hover media queries (more reliable than UA
      // sniffing for hybrid touch + mouse setups).
      const isCoarsePointer = typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        (window.matchMedia("(pointer: coarse)").matches ||
         window.matchMedia("(hover: none)").matches);
      if (!isCoarsePointer) {
        setTimeout(() => {
          if (!editor.isDestroyed && !editor.isFocused) {
            editor.commands.focus("start");
          }
        }, 300);
      }
    }
    return () => {
      setActiveEditor(null);
      setEditorInstance(null);
      if (editor) unregisterTiptap(editor);
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [editor, setActiveEditor, setEditorInstance, registerTiptap, unregisterTiptap]);

  /**
   * A read-only probe for browser tests, under VITE_E2E only.
   *
   * Some claims cannot be checked from the DOM. Whether the editor was *moved*
   * or rebuilt is one: the text looks identical either way, and what separates
   * them is the selection and the undo stack, which live in ProseMirror state.
   * Clicking a tab also takes DOM focus, so the browser's own selection is gone
   * by the time a test could read it.
   *
   * Deliberately not the editor itself — reads only, so a test cannot quietly
   * start driving the app through a back door. Dead code in a production build,
   * where the env flag is not set.
   */
  useEffect(() => {
    if (import.meta.env.VITE_E2E !== "true" || !editor) return;
    const w = window as unknown as { __liliaEditorProbe?: unknown };
    w.__liliaEditorProbe = {
      selection: () => ({ from: editor.state.selection.from, to: editor.state.selection.to }),
      undoDepth: () => {
        for (const [key, value] of Object.entries(
          editor.state as unknown as Record<string, unknown>)) {
          if (!key.startsWith("history$")) continue;
          const done = (value as { done?: { eventCount?: number } })?.done;
          if (typeof done?.eventCount === "number") return done.eventCount;
        }
        return 0;
      },
      text: () => editor.getText(),
    };
    return () => { delete w.__liliaEditorProbe; };
  }, [editor]);

  // When blocks change externally (e.g., import, version restore), update editor content
  const blocksVersionRef = useRef(0);
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // Skip the initial render (editor was already initialized with blocks)
    if (blocksVersionRef.current === 0) {
      blocksVersionRef.current = 1;
      return;
    }

    // Only update if blocks changed externally (not from our own edits)
    const isDirty = useEditorStore.getState().isDirty;
    if (!isDirty) {
      const newContent = blocksToTiptapDoc(blocks);
      editor.commands.setContent(newContent);
      // setContent does not always fire onUpdate; keep the empty-hint in sync.
      setEditorEmpty(editor.isEmpty);
    }
  }, [blocks, editor]);

  // REGRESSION-002 — drain server-only blocks the rebase queued for us.
  // Surgical insert (not setContent) so any keystrokes the user typed
  // between the rebase fetch and this effect aren't wiped. We also
  // pin the user's selection across the insert: insertContentAt at
  // the end of the doc would otherwise move the caret INTO the new
  // paragraph, so any further typing fragments across two blocks.
  const pendingRebaseInserts = useEditorStore((s) => s.pendingRebaseInserts);
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (pendingRebaseInserts.length === 0) return;
    const endPos = editor.state.doc.content.size;
    const nodes = pendingRebaseInserts
      .map((b) => blockToTiptapNode(b))
      .filter((n): n is JSONContent => n != null);
    if (nodes.length > 0) {
      const { from, to } = editor.state.selection;
      editor.commands.insertContentAt(endPos, nodes);
      // Restore selection — `from`/`to` were positions BEFORE endPos
      // (the user was editing somewhere in the existing content), so
      // they still index the same logical spot after the append.
      // Clamp to the new doc size as a defensive guard.
      const newSize = editor.state.doc.content.size;
      const safeFrom = Math.min(from, newSize);
      const safeTo = Math.min(to, newSize);
      editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
    }
    // Clear the queue. Direct setState — this isn't a user action.
    useEditorStore.setState({ pendingRebaseInserts: [] });
  }, [pendingRebaseInserts, editor]);

  // The rest of the rebase: blocks the server deleted and blocks it converted
  // or edited, where this editor had not touched them (mergeOnRebase decides).
  // One transaction, last position first, so earlier positions stay valid and
  // the caret is mapped through the change rather than reset.
  const pendingRebaseRemovals = useEditorStore((s) => s.pendingRebaseRemovals);
  const pendingRebaseReplacements = useEditorStore((s) => s.pendingRebaseReplacements);
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (pendingRebaseRemovals.length === 0 && pendingRebaseReplacements.length === 0) return;
    applyRebase(editor, pendingRebaseRemovals, pendingRebaseReplacements, blockToTiptapNode);
    useEditorStore.setState({ pendingRebaseRemovals: [], pendingRebaseReplacements: [] });
  }, [pendingRebaseRemovals, pendingRebaseReplacements, editor]);

  // The slash menu follows the SlashTrigger plugin: it opens on a `/` typed
  // at the start of a line or after a space, and closes when the trigger goes.
  useEffect(() => {
    if (!editor) return;
    const read = () => {
      const t = readSlashTrigger(editor.state);
      setSlashState((prev) => {
        if (!t.active) return prev.show ? { show: false, query: "", anchor: null, caretCoords: null } : prev;
        if (prev.show && prev.query === t.query) return prev;
        const coords = editor.view.coordsAtPos(t.to);
        return {
          show: true,
          query: t.query,
          anchor: editor.view.dom as HTMLElement,
          caretCoords: { top: coords.top, left: coords.left, bottom: coords.bottom },
        };
      });
    };
    editor.on("transaction", read);
    return () => { editor.off("transaction", read); };
  }, [editor]);

  /** Every row, before it runs: the `/` and the filter text go. */
  const handleSlashConsume = useCallback(() => {
    if (editor) consumeSlashTrigger(editor);
  }, [editor]);

  /**
   * A block row. Its trigger is already consumed: on an empty line the block
   * replaces the paragraph, mid-line it goes after the line.
   */
  const handleSlashSelect = useCallback((blockType: BlockType) => {
    if (!editor) return;
    const nodeType = BLOCK_TYPE_TO_NODE[blockType];
    if (!nodeType) return;

    const node: JSONContent =
      blockType === "paragraph"
        ? { type: "paragraph", attrs: { blockId: generateUUID() } }
        : blockType === "heading"
          ? { type: "heading", attrs: { level: 1, blockId: generateUUID() } }
          : { type: nodeType, attrs: getInsertAttrs(blockType) };

    const range = takeSlashInsertRange(editor) ?? afterCurrentBlock(editor);
    editor.chain().focus().insertContentAt(range, node).run();
  }, [editor]);

  /** Esc or a click away: the menu closes and the text stays as typed. */
  const handleSlashClose = useCallback(() => {
    if (editor) dismissSlashTrigger(editor);
    setSlashState({ show: false, query: "", anchor: null, caretCoords: null });
  }, [editor]);

  /** Insert a block from the + menu or AI */
  const insertBlock = useCallback((blockType: BlockType, content?: Record<string, unknown>, afterBlockId?: string) => {
    if (!editor) return;

    const nodeType = BLOCK_TYPE_TO_NODE[blockType];
    if (!nodeType) return;

    // Insert right after the cursor's parent block so the user's
    // current paragraph stays whole and the new block appears just
    // below. Falls back to end-of-doc when there's no live selection
    // (empty doc, never-focused editor).
    const { selection, doc } = editor.state;
    const $from = selection.$from;
    let insertPos = doc.content.size;
    if ($from.depth >= 1) {
      try {
        insertPos = $from.after(1);
      } catch {
        // $from.after can throw at boundary positions — fall back.
      }
    }
    // A figure Ask Lilia drew goes where it was asked for (Draw with Ask Lilia…).
    if (afterBlockId) {
      doc.forEach((node, offset) => {
        if (node.attrs.blockId === afterBlockId) insertPos = offset + node.nodeSize;
      });
    }

    if (blockType === "paragraph") {
      editor.chain().focus().insertContentAt(insertPos, {
        type: "paragraph",
        attrs: { blockId: generateUUID() },
        content: content?.text ? [{ type: "text", text: content.text as string }] : [],
      }).run();
    } else if (blockType === "heading") {
      const level = (content?.level as number) || 1;
      editor.chain().focus().insertContentAt(insertPos, {
        type: "heading",
        attrs: { level, blockId: generateUUID() },
        content: content?.text ? [{ type: "text", text: content.text as string }] : [],
      }).run();
    } else {
      const attrs = { blockId: generateUUID(), ...content || getDefaultContent(blockType) };
      editor.chain().focus().insertContentAt(insertPos, {
        type: nodeType,
        attrs,
      }).run();
    }
  }, [editor]);

  // Keyboard shortcuts: Cmd/Ctrl+Shift+G for grammar, Delete focused block.
  // (AI assistant + Cmd/Ctrl+K shortcut deactivated for launch — Pro
  // re-activation tracked under the post-launch backlog.)
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Delete/Backspace on a focused non-text block — only for a key that
      // arrives from inside the canvas. It listened on the whole window, so
      // Backspace anywhere focus sat outside a text field (a dialog, a menu,
      // a panel button) deleted whatever block was focused: in the equation
      // modal it removed the equation being edited.
      if ((e.key === "Delete" || e.key === "Backspace") && !e.metaKey && !e.ctrlKey) {
        const target = e.target as HTMLElement;
        const isEditable = target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA";
        const inCanvas = !!target.closest?.('[data-testid="flow-editor"]');
        const inOverlay = !!target.closest?.('[role="dialog"], [role="menu"], [role="listbox"]');
        if (!isEditable && inCanvas && !inOverlay) {
          const { focusedBlockId } = useEditorStore.getState();
          if (focusedBlockId) {
            e.preventDefault();
            useEditorStore.getState().deleteBlock(focusedBlockId);
            return;
          }
        }
      }
      // Cmd/Ctrl+D — duplicate the focused block (works while editing too;
      // deletion is likewise keyboard-driven, so this keeps the model consistent).
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === "d" || e.key === "D")) {
        const { focusedBlockId } = useEditorStore.getState();
        if (focusedBlockId) {
          e.preventDefault();
          useEditorStore.getState().duplicateBlock(focusedBlockId);
          return;
        }
      }
      // With Shift held the key is "G": checking "g" alone never fired.
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "g") {
        e.preventDefault();
        if (!editor) return;
        // Extract paragraph text from TipTap document
        const doc = editor.getJSON();
        const paragraphTexts = (doc.content || [])
          .filter((n) => n.type === "paragraph")
          .map((n) => {
            return (n.content || [])
              .map((c) => (c as { text?: string }).text || "")
              .filter(Boolean)
              .join("");
          })
          .filter(Boolean)
          .join("\n\n");
        if (paragraphTexts) grammar.check(paragraphTexts);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editor, grammar]);

  // AI assistant handlers removed — feature deactivated for launch.
  // Restoration plan: re-import useAIAssistant + AIAssistantPanel,
  // reinstate handleAIInsert / handleAIReplace, mount the panel
  // gated on `user.plan_tier === 'pro'` once the gates ship.

  // "openImportLatex" (slash menu's Paste LaTeX…, toolbox, add-block menu) is
  // answered by the host page — EditorPage owns the one import dialog. This
  // editor used to open its own as well, so the slash command stacked two.

  // Listen for ToolboxMount insert events (final-road v1 §C2 — entry points
  // converge on a single CustomEvent so rail/slash/⌘//Toolbox all share
  // the same insertBlock path).
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { call?: { type?: string; content?: Record<string, unknown> }; afterBlockId?: string; openSplit?: boolean }
        | undefined;
      const type = detail?.call?.type;
      if (!type) return;
      // Insert → Figure → TikZ: the new figure opens its split view on the gallery
      // (Ask Lilia's "Open in split view": on its source).
      if (type === "figure" && detail?.call?.content?.kind === "tikz") {
        const source = String(detail.call.content.source ?? "");
        if (!source.trim() || detail.openSplit) requestTikzSplitOnInsert(source);
      }
      insertBlock(type as BlockType, detail?.call?.content, detail?.afterBlockId);
    };
    window.addEventListener("lilia:toolbox-insert", handler);
    return () => window.removeEventListener("lilia:toolbox-insert", handler);
  }, [insertBlock]);

  // Insert → Figure → Draw with Ask Lilia… (TikZ 1c): the dock, scoped to a new
  // figure where the cursor is ("New figure · §3.2"), with "Draw " in its input.
  useEffect(() => {
    const handler = () => {
      if (!editor || !documentId) return;
      const nodes = outlineOf(editor.state.doc);
      const $from = editor.state.selection.$from;
      const index = $from.depth >= 1 ? $from.index(0) : nodes.length - 1;
      const after = nodes[Math.min(index, nodes.length - 1)];
      setFigureScope({
        documentId,
        blockId: null,
        afterBlockId: after?.blockId ?? null,
        section: sectionAt(nodes, index),
        owner: "insert",
      });
      prefillAsk("Draw ");
    };
    window.addEventListener(TIKZ_DRAW_WITH_ASK_EVENT, handler);
    return () => window.removeEventListener(TIKZ_DRAW_WITH_ASK_EVENT, handler);
  }, [editor, documentId]);

  // Toolbox inline-kind entries — `lilia:toolbox-mark` is the single
  // event for any insertion that needs direct editor access (math
  // wrap, citation node, cross-ref node, footnote node). ToolboxMount
  // dispatches with variant=<kind|variant>; this handler routes each
  // to the right insertContent call on the live editor.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as { variant?: string } | undefined;
      if (!editor) return;
      const variant = detail?.variant ?? "";

      if (variant === "math") {
        const { from, to, empty } = editor.state.selection;
        if (empty) {
          editor.chain().focus().insertContent("$  $").run();
          editor.chain().focus().setTextSelection(from + 2).run();
        } else {
          const selected = editor.state.doc.textBetween(from, to);
          editor.chain().focus().insertContent(`$${selected}$`).run();
        }
        return;
      }
      // References and citations go through the one @ picker — it lists what
      // the document and library actually hold, where a prompt asked the
      // author to remember a key. `@cite{` is the picker's library filter.
      if (variant === "citation") {
        editor.chain().focus().insertContent("@cite{").run();
        return;
      }
      if (variant === "cross-ref" || variant === "reference") {
        editor.chain().focus().insertContent("@").run();
        return;
      }
      if (variant === "footnote") {
        const content = window.prompt("Footnote text:");
        if (!content) return;
        editor.chain().focus().insertContent({ type: "footnote", attrs: { content } }).run();
        return;
      }
    };
    window.addEventListener("lilia:toolbox-mark", handler);
    return () => window.removeEventListener("lilia:toolbox-mark", handler);
  }, [editor]);

  // Batch insert handler — lorem surfaces (chip / seed / header) emit
  // one event with N paragraphs so all inserts land in a single
  // ProseMirror transaction. Result: one Cmd+Z undoes the whole batch
  // (Engineering brief §C4 acceptance A1.8).
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | Partial<ToolboxInsertBatchEventDetail>
        | undefined;
      const calls = detail?.calls;
      if (!editor || !calls || calls.length === 0) return;

      // One position for the whole batch: where the `/lorem` was when the
      // slash menu sent it, otherwise just after the current block.
      const at = (detail?.atSlash ? takeSlashInsertRange(editor) : null) ?? afterCurrentBlock(editor);

      const nodes = calls
        .filter((c) => c.type === "paragraph")
        .map((c) => ({
          type: "paragraph",
          attrs: { blockId: generateUUID() },
          content: c.content?.text
            ? [{ type: "text", text: c.content.text as string }]
            : [],
        }));

      if (nodes.length === 0) return;
      // Single chain → single transaction → single undo step.
      editor.chain().focus().insertContentAt(at, nodes).run();
    };
    window.addEventListener("lilia:toolbox-insert-batch", handler);
    return () => window.removeEventListener("lilia:toolbox-insert-batch", handler);
  }, [editor]);

  // Visual highlight for focused block (from canvas click OR source line click).
  // Scroll into view when focus comes from outside (source ↔ content sync).
  const focusedBlockId = useEditorStore((s) => s.focusedBlockId);

  // Track focused block from DOM clicks (catches node-view clicks that TipTap doesn't)
  useEffect(() => {
    const container = editorRef.current;
    if (!container) return;
    function handleClick(e: MouseEvent) {
      let el = e.target as HTMLElement | null;
      while (el && el !== container) {
        const blockId = el.getAttribute("data-block-id");
        if (blockId) {
          lastLocalFocusClickRef.current = Date.now();
          useEditorStore.getState().setFocusedBlockId(blockId);
          return;
        }
        el = el.parentElement;
      }
    }
    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    const container = editorRef.current;
    if (!container) return;
    const prev = container.querySelector("[data-block-focused]");
    if (prev) prev.removeAttribute("data-block-focused");
    if (focusedBlockId) {
      const el = container.querySelector(`[data-block-id="${focusedBlockId}"]`);
      if (el) {
        el.setAttribute("data-block-focused", "true");
        // If focus was set from the source pane (not a click we just handled),
        // bring the block into the canvas scrollport.
        if (Date.now() - lastLocalFocusClickRef.current > 150) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  }, [focusedBlockId]);

  // Listen for "focusEditor" events from preview
  useEffect(() => {
    const handleFocusEditor = () => {
      editor?.commands.focus();
    };
    window.addEventListener("focusEditor", handleFocusEditor);
    return () => window.removeEventListener("focusEditor", handleFocusEditor);
  }, [editor]);

  return (
    <div className={styles.editor} ref={editorRef}>
      {/* The sticky kicker (document themes, 2d): before the scroller, so Tab
          reaches it after the top bar; it sits 12 px below the canvas top,
          inside the canvas, so it never covers the mega bar above. */}
      <CanvasKicker blocks={blocks} look={look} scrollRef={scrollRef} />
      <div className={styles.scrollContainer} ref={scrollRef}>
        {/* The page (document themes, REPLY-2026-10-06d §2): it takes the
            look's paper and ink; the surround stays editor chrome. A box-less
            wrapper, so `flow-editor` keeps its testid and its layout. */}
        <div
          className={styles.canvasPage}
          data-testid={testIds.look.canvasPage}
          {...canvasPageAttrs(look, darkEditor)}
        >
          <div
            className={`${styles.blockList} ${isEmpty ? styles.isEmpty : ""}`}
            data-testid="flow-editor"
            data-look-theme={look?.theme}
            data-paper-tinted={look?.paperTinted ? "true" : undefined}
            data-look-ink={look?.vars["--lilia-look-ink"] ? "true" : undefined}
            style={look?.vars as CSSProperties | undefined}
          >
            {look?.css ? <style data-testid={testIds.look.canvasStyle}>{look.css}</style> : null}
            {editor ? (
              <EditorContent editor={editor} />
            ) : (
              <div className={styles.empty}>
                <p>Loading editor...</p>
              </div>
            )}
            {/* Flow-mode empty-state ghost hint (Claude Design handoff 2026-06-17).
                A ghost line that belongs to the cursor's first line: invites
                writing and surfaces the "/" slash-command. Non-blocking — the
                caret is already on the line, so typing dismisses it. The "/" chip
                is the only interactive part; it opens the same inserter as typing
                "/". The default placeholder is hidden while this shows. */}
            {editor && (
              <div
                className={`${styles.emptyHint} ${isEmpty ? "" : styles.emptyHintHidden}`}
                data-testid="flow-empty-hint"
                aria-hidden="true"
              >
                <span className={styles.emptyHintWrite}>Start writing</span>
                <span className={styles.emptyHintRest}>or press</span>
                <button
                  type="button"
                  data-testid="flow-slash-chip"
                  aria-label="Insert a block"
                  className={styles.emptyHintChip}
                  onMouseDown={(e) => {
                    // preventDefault keeps the caret on the line; insert "/" so the
                    // SlashTrigger plugin opens the inserter — same as typing.
                    e.preventDefault();
                    editor.chain().focus().insertContent("/").run();
                  }}
                >
                  /
                </button>
                <span className={styles.emptyHintRest}>for a block</span>
              </div>
            )}
            {/* Status whisper (Pass 3 / Implementation guidance #6).
                Lives below the document body inside the scroll container,
                not as chrome. Reads its data from editorStore.statusMeta,
                pushed by EditorPage's useEffect. Hidden if no doc loaded. */}
            <BlockListStatusWhisper />
          </div>
        </div>
      </div>

      {/* Mobile selection popover (Claude Design 2026-05-09 direction D).
          iOS-native style format chip bar above the selection. Hides
          itself on desktop via internal matchMedia check. Link demoted
          here from the keyboard accessory per the §A trim. */}
      <MobileSelectionPopover
        editor={editor ?? null}
        onLink={() => {
          if (!editor) return;
          const url = window.prompt("Enter URL:");
          if (!url) return;
          editor.chain().focus().insertContent({ type: "inlineUrl", attrs: { href: url } }).run();
        }}
        onCite={() => {
          if (!editor) return;
          const key = window.prompt("Enter citation key:");
          if (!key) return;
          editor.chain().focus().insertContent({ type: "citation", attrs: { citeKey: key } }).run();
        }}
        // onMore + MobileMoreFormatSheet removed PF.T02.
      />

      <SlashCommandMenu
        anchor={slashState.anchor}
        show={slashState.show}
        query={slashState.query}
        onSelect={handleSlashSelect}
        onClose={handleSlashClose}
        onConsume={handleSlashConsume}
        caretCoords={slashState.caretCoords}
        commands={[loremSlashCommand, existingTableSlashCommand]}
      />

      {/* One `@`, over this document's labels and the library. It replaces the
          citation-only picker, whose `@cite{` is now a filter on this one. */}
      {pasteFix.chip && (
        <PasteFixChip chip={pasteFix.chip} onLookUp={lookUpSymbol} onDismiss={pasteFix.dismiss} />
      )}

      <RefPicker
        editor={editor}
        documentId={document?.id}
        fetchWithAuth={fetchWithAuth}
        library={libraryForPicker}
      />

      {/* EditorContextMenu + FloatingToolbar: not mounted on Flow.
          Right-click = browser default. Format via ribbon / ⌘K / shortcuts. */}

      {/* AIAssistantPanel removed entirely 2026-04-28 for launch.
          Component file kept in src/components/editor/AIAssistantPanel.tsx
          + hook in src/hooks/useAIAssistant.ts so post-launch revival
          under Pro is just an import + mount restoration. */}

      <GrammarPanel
        isOpen={grammar.state.isOpen || grammar.state.suggestions.length > 0 || grammar.state.isLoading}
        isLoading={grammar.state.isLoading}
        suggestions={grammar.state.suggestions}
        error={grammar.state.error}
        onClose={grammar.close}
        onApply={(index) => {
          const s = grammar.apply(index);
          if (s && editor) replaceFirst(editor, s.original, s.suggestion);
        }}
        onDismiss={grammar.dismiss}
      />
    </div>
  );
}

// ===== Source content functions (kept for backward compatibility with showSourceTabs) =====

interface BibEntry {
  id: string;
  citeKey: string;
  entryType: string;
  fields: Record<string, string>;
}

function convertToLatex(text: string): string {
  if (!text) return text;
  let result = text;
  result = result.replace(/\*\*([^*]+)\*\*/g, "\\textbf{$1}");
  result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "\\textit{$1}");
  result = result.replace(/__([^_]+)__/g, "\\underline{$1}");
  result = result.replace(/~~([^~]+)~~/g, "\\sout{$1}");
  result = result.replace(/`([^`]+)`/g, "\\texttt{$1}");
  return result;
}

function convertToLml(text: string): string {
  if (!text) return text;
  let result = text;
  result = result.replace(/\*\*([^*]+)\*\*/g, "@b{$1}");
  result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "@i{$1}");
  result = result.replace(/__([^_]+)__/g, "@u{$1}");
  result = result.replace(/~~([^~]+)~~/g, "@s{$1}");
  result = result.replace(/`([^`]+)`/g, "@code{$1}");
  result = result.replace(/\$([^$]+)\$/g, "@m{$1}");
  return result;
}

export function getSourceContent(block: Block, bibliographyEntries?: BibEntry[]): ReactNode {
  const content = block.content || {};
  switch (block.type) {
    case "equation": {
      const latex = (content.latex as string) || "";
      const mode = (content.equationMode as string) || "display";
      const label = (content.label as string) || "";
      return `% Equation (${mode} mode)${label ? `\n% Label: ${label}` : ""}\n${latex || "(empty)"}`;
    }
    case "paragraph": {
      const text = (content.text as string) || "";
      return convertToLatex(text) || "(empty paragraph)";
    }
    case "heading": {
      const text = (content.text as string) || "";
      const level = (content.level as number) || 1;
      const cmds = ["section", "subsection", "subsubsection", "paragraph", "subparagraph"];
      const cmd = cmds[Math.min(level - 1, cmds.length - 1)];
      return `\\${cmd}{${text || "(empty heading)"}}`;
    }
    default:
      return JSON.stringify(content, null, 2);
  }
}

/**
 * Status whisper rendered at the foot of the document scroll container.
 * Reads metadata from editorStore.statusMeta (pushed by EditorPage); falls
 * back to silence when no doc is loaded.
 */
function BlockListStatusWhisper() {
  const meta = useEditorStore((s) => s.statusMeta);
  const readOnly = !useCanEdit();
  const blocks = useEditorStore((s) => s.blocks);
  if (!meta) return null;
  // Unsaved = the sync's pending edits. The store's isDirty is set by every
  // editor update and never cleared in Flow (it gates external setContent
  // below), so the whisper said "Unsaved changes" on a saved — even an
  // untouched — document while the top bar said Saved.
  return (
    <StatusBar
      saveStatus={meta.saveStatus}
      readOnly={readOnly}
      isDirty={meta.hasUnsaved}
      blockCount={blocks.length}
      wordCount={meta.wordCount}
      currentSection={meta.currentSection}
      lastSavedAt={meta.lastSavedAt}
    />
  );
}
